// QuickAudit.tsx — public, no-login Google Maps geo-grid audit (Kaliber-style).
// Flow: enter business + city + keyword → search → pick branch → run 3x3 scan →
// poll → render an interactive Leaflet map + scorecards + per-point competitor
// rankings (mirrors the internal GBI dashboard). Add #demo to the URL to render
// a sample (real MX Clinic) result without calling the API or spending credits.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import L from 'leaflet';
import { T, sharedStyles, rankColor } from '../components/Shared';
import { useIsMobile } from '../lib/useIsMobile';
import { DEMO_REPORT, type QAReport, type QAGridPoint } from './quickAuditDemo';

interface Candidate {
  place_id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  rating: string;
  reviews: number;
}

type Phase = 'input' | 'searching' | 'picking' | 'scanning' | 'done' | 'error';

const POLL_MS = 6000;
const MAX_POLL_MS = 4 * 60 * 1000; // give up after 4 min

export function QuickAudit() {
  const isDemo = typeof window !== 'undefined' && window.location.hash === '#demo';

  const [phase, setPhase] = useState<Phase>(isDemo ? 'done' : 'input');
  const [name, setName] = useState('');
  const [proximity, setProximity] = useState('');
  const [keyword, setKeyword] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [report, setReport] = useState<QAReport | null>(isDemo ? DEMO_REPORT : null);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);

  const pollStart = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  async function postJson<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error || `Request failed (${res.status})`);
    return data as T;
  }

  async function startAudit() {
    setError('');
    if (name.trim().length < 2) { setError('Enter your business name.'); return; }
    if (keyword.trim().length < 2) { setError('Enter a keyword you want to rank for.'); return; }
    setPhase('searching');
    try {
      const { results } = await postJson<{ results: Candidate[] }>(
        '/api/quick-audit/search', { name, proximity },
      );
      if (results.length === 0) {
        setError('No Google Business listing found. Try adding the city, or check the spelling.');
        setPhase('input');
        return;
      }
      if (results.length === 1) {
        runScan(results[0]);
      } else {
        setCandidates(results);
        setPhase('picking');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed.');
      setPhase('input');
    }
  }

  async function runScan(c: Candidate) {
    setError('');
    setPhase('scanning');
    setElapsed(0);
    pollStart.current = Date.now();
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - pollStart.current) / 1000)), 1000);
    try {
      const { report_key } = await postJson<{ report_key: string }>(
        '/api/quick-audit/run', { place_id: c.place_id, keyword, lat: c.lat, lng: c.lng },
      );
      pollReport(report_key);
    } catch (e) {
      if (tickRef.current) clearInterval(tickRef.current);
      setError(e instanceof Error ? e.message : 'Could not start the scan.');
      setPhase('error');
    }
  }

  async function pollReport(key: string) {
    try {
      const res = await fetch(`/api/quick-audit/report/${encodeURIComponent(key)}`);
      const data = await res.json() as { status?: string; report?: QAReport; error?: string };
      if (data.status === 'ready' && data.report) {
        if (tickRef.current) clearInterval(tickRef.current);
        setReport(data.report);
        setPhase('done');
        return;
      }
      if (data.error) throw new Error(data.error);
    } catch (e) {
      if (tickRef.current) clearInterval(tickRef.current);
      setError(e instanceof Error ? e.message : 'Failed while fetching the report.');
      setPhase('error');
      return;
    }
    if (Date.now() - pollStart.current > MAX_POLL_MS) {
      if (tickRef.current) clearInterval(tickRef.current);
      setError('The scan is taking longer than usual. It may still finish — try again in a minute.');
      setPhase('error');
      return;
    }
    setTimeout(() => pollReport(key), POLL_MS);
  }

  function reset() {
    if (tickRef.current) clearInterval(tickRef.current);
    setPhase('input'); setCandidates([]); setReport(null); setError(''); setElapsed(0);
  }

  const wide = phase === 'done';

  return (
    <div style={{ minHeight: '100vh', background: T.bgSurface, fontFamily: sharedStyles.fontBody, color: T.fgPrimary }}>
      <div style={{ maxWidth: wide ? 1080 : 760, margin: '0 auto', padding: '48px 20px 40px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.5, color: T.fgMuted, textTransform: 'uppercase' }}>
            Google Maps · Geo-grid audit
          </span>
        </div>
        <h1 style={{ textAlign: 'center', fontFamily: sharedStyles.fontDisplay, fontSize: 40, fontWeight: 800, lineHeight: 1.1, margin: '8px 0 16px' }}>
          Where do you rank on{' '}
          <span style={{ background: T.brandGradient, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            Google Maps?
          </span>
        </h1>
        <p style={{ textAlign: 'center', fontSize: 16, color: T.fgSecondary, maxWidth: 560, margin: '0 auto 28px', lineHeight: 1.55 }}>
          See your real Google Maps ranking across a 3×3 geo-grid around your business — exactly where you show up on the map, in ~2 minutes.
        </p>

        {(phase === 'input' || phase === 'searching') && (
          <Card>
            <Field label="Business name">
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)}
                placeholder="MX Clinic Signature" disabled={phase === 'searching'}
                onKeyDown={e => e.key === 'Enter' && startAudit()} />
            </Field>
            <Field label="City / area (recommended)">
              <input style={inputStyle} value={proximity} onChange={e => setProximity(e.target.value)}
                placeholder="Johor Bahru" disabled={phase === 'searching'}
                onKeyDown={e => e.key === 'Enter' && startAudit()} />
            </Field>
            <Field label="Keyword to rank for">
              <input style={inputStyle} value={keyword} onChange={e => setKeyword(e.target.value)}
                placeholder="skin specialist johor bahru" disabled={phase === 'searching'}
                onKeyDown={e => e.key === 'Enter' && startAudit()} />
            </Field>
            {error && <ErrorNote text={error} />}
            <button style={primaryBtn(phase === 'searching')} onClick={startAudit} disabled={phase === 'searching'}>
              {phase === 'searching' ? 'Searching…' : 'Run free audit'}
            </button>
            <div style={{ textAlign: 'center', fontSize: 12, color: T.fgMuted, marginTop: 12 }}>
              Google Maps · 3×3 grid · ~2 min
            </div>
          </Card>
        )}

        {phase === 'picking' && (
          <Card>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>We found a few matches — pick yours:</div>
            <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 16 }}>Keyword: <b>{keyword}</b></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {candidates.map(c => (
                <button key={c.place_id} onClick={() => runScan(c)} style={candidateCard}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                  <div style={{ fontSize: 12.5, color: T.fgSecondary, marginTop: 2 }}>{c.address}</div>
                  <div style={{ fontSize: 12, color: T.fgMuted, marginTop: 4 }}>★ {Number(c.rating).toFixed(1)} · {c.reviews} reviews</div>
                </button>
              ))}
            </div>
            <button style={{ ...ghostBtn, marginTop: 16 }} onClick={reset}>← Start over</button>
          </Card>
        )}

        {phase === 'scanning' && <ScanningCard elapsed={elapsed} keyword={keyword} />}

        {phase === 'error' && (
          <Card>
            <ErrorNote text={error} />
            <button style={{ ...primaryBtn(false), marginTop: 8 }} onClick={reset}>Try again</button>
          </Card>
        )}

        {phase === 'done' && report && <Results report={report} onReset={reset} />}
      </div>

      <Footer />
    </div>
  );
}

// ── Results — map + scorecards + competitor panel ─────────────────────
function Results({ report: r, onReset }: { report: QAReport; onReset: () => void }) {
  const isMobile = useIsMobile();
  const total = Number(r.points) || r.grid.length || 9;
  const foundIn = Number(r.found_in);
  const blind = Number.isFinite(foundIn) ? Math.max(0, total - foundIn) : null;
  const solvNum = Number(r.solv);
  const arpNum = Number(r.arp);
  // Default-select the center point so the panel shows immediately (like the
  // internal dashboard). Falls back to the first point.
  const [selected, setSelected] = useState<number>(Math.floor(r.grid.length / 2));
  const point = r.grid[selected];

  return (
    <div>
      <Card>
        {/* Business header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{r.business.name}</div>
            <div style={{ fontSize: 13, color: T.fgSecondary, marginTop: 2 }}>{r.business.address}</div>
            <div style={{ fontSize: 12.5, color: T.fgMuted, marginTop: 4 }}>
              Keyword: <b style={{ color: T.fgSecondary }}>{r.keyword}</b> · {r.grid_size}×{r.grid_size} grid · {r.radius}{r.measurement}
            </div>
          </div>
          {r.business.rating && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.brandAmber }}>★ {Number(r.business.rating).toFixed(1)}</div>
              <div style={{ fontSize: 12, color: T.fgMuted }}>{r.business.reviews} reviews</div>
            </div>
          )}
        </div>

        {/* Scorecards */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12, margin: '20px 0' }}>
          <Stat label="Avg Rank Position" value={Number.isFinite(arpNum) ? arpNum.toFixed(2) : '—'} color="#188038" sub={`Across ${total} points`} />
          <Stat label="Share of Local Voice" value={Number.isFinite(solvNum) ? `${solvNum.toFixed(1)}%` : '—'} color={T.cyan} sub="In Map Pack" />
          <Stat label="Found In" value={Number.isFinite(foundIn) ? `${foundIn}/${total}` : '—'} color={T.yellow} sub={Number.isFinite(foundIn) ? `${Math.round((foundIn / total) * 100)}% coverage` : '—'} />
          <Stat label="Blind Spots" value={blind != null ? String(blind) : '—'} color={T.magenta} sub="Not in Map Pack" />
        </div>

        {/* Map + competitor panel */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: isMobile ? 'stretch' : 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0, borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
            <QAMap report={r} selected={selected} onSelect={setSelected} />
            <div style={{ padding: '12px 16px', background: T.bgSurface, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              {[
                { color: '#188038', label: '#1 (top)' },
                { color: '#34a853', label: '#2–3' },
                { color: '#fbbc04', label: '#4–7' },
                { color: '#e37400', label: '#8–9' },
                { color: '#d93025', label: '10+ (weak)' },
                { color: '#9aa0a6', label: 'Not found' },
              ].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.fgMuted }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: l.color }} />{l.label}
                </div>
              ))}
            </div>
          </div>

          {point && <RankingsPanel point={point} youPlaceId={r.place_id} youName={r.business.name} />}
        </div>

        {/* Actions — supplier report links are intentionally hidden. */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
          <button onClick={onReset} style={ghostBtn}>Run another</button>
        </div>
      </Card>

      <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: T.fgSecondary, lineHeight: 1.6 }}>
        Want this tracked weekly with competitor moves and AI-search visibility?<br />
        <b style={{ color: T.fgPrimary }}>Talk to YEA Business about a full GBP monitoring plan.</b>
      </div>
    </div>
  );
}

// ── Leaflet map: one marker per grid point ────────────────────────────
function QAMap({ report, selected, onSelect }: { report: QAReport; selected: number; onSelect: (i: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const centerLat = report.center?.lat || report.grid[0]?.lat || 0;
    const centerLng = report.center?.lng || report.grid[0]?.lng || 0;

    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const map = L.map(containerRef.current, {
      center: [centerLat, centerLng], zoom: 12,
      zoomControl: true, attributionControl: true, scrollWheelZoom: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>', maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    const bounds = L.latLngBounds([]);
    report.grid.forEach(p => {
      const rc = rankColor(p.rank);
      const bg = p.rank > 0 ? rc.bg : '#9aa0a6';
      const fg = p.rank > 0 ? rc.color : '#fff';
      const isTop = p.rank === 1;
      const html = `<div style="width:32px;height:32px;border-radius:50%;background:${bg};display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:700;color:${fg};box-shadow:${isTop ? '0 0 0 3px #fff,0 0 12px rgba(24,128,56,0.6)' : '0 2px 6px rgba(0,0,0,0.2),0 0 0 2px rgba(255,255,255,0.85)'};cursor:pointer;">${p.rank > 20 ? '20+' : p.rank > 0 ? p.rank : 'X'}</div>`;
      const icon = L.divIcon({ className: '', html, iconSize: [32, 32], iconAnchor: [16, 16] });
      const marker = L.marker([p.lat, p.lng], { icon }).addTo(map);
      marker.bindTooltip(`#${p.index + 1} · Rank ${p.rank > 0 ? p.rank : 'not found'}`, { direction: 'top', offset: [0, -6] });
      marker.on('click', () => onSelect(p.index));
      bounds.extend([p.lat, p.lng]);
    });
    if (report.grid.length > 0) map.fitBounds(bounds, { padding: [40, 40] });

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  // Rebuild only when the underlying scan changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.report_key]);

  return <div ref={containerRef} style={{ height: 460, width: '100%' }} />;
}

// ── Competitor rankings panel for the selected point ──────────────────
function RankingsPanel({ point, youPlaceId, youName }: { point: QAGridPoint; youPlaceId: string; youName?: string }) {
  const inTop = point.results.some(c => c.place_id === youPlaceId);
  return (
    <div style={{
      width: 380, maxWidth: '100%', flexShrink: 0, background: '#fff',
      border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Rankings at point #{point.index + 1}</div>
        <div style={{ fontSize: 11, color: T.fgMuted, fontFamily: sharedStyles.fontMono, marginTop: 2 }}>
          {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
        </div>
      </div>
      <div style={{ padding: '14px 16px', overflowY: 'auto' }}>
        {point.results.length === 0 ? (
          <div style={{ fontSize: 12, color: T.fgMuted, padding: '20px 0' }}>No business ranked in the top 20 here.</div>
        ) : (
          <>
            <SectionHeader>Top {point.results.length} in Map Pack</SectionHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {point.results.map(c => (
                <RankingRow key={c.place_id} rank={c.rank} name={c.name} rating={c.rating} reviews={c.reviews} category={c.category} isYou={c.place_id === youPlaceId} />
              ))}
            </div>
            {!inTop && (
              point.rank > 0 ? (
                <>
                  <div style={{ height: 12 }} />
                  <SectionHeader>Your position</SectionHeader>
                  <RankingRow rank={point.rank} name={youName || 'Your business'} rating={null} reviews={null} category="" isYou />
                </>
              ) : (
                <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(232,80,26,0.06)', border: `1px solid ${T.magenta}`, borderRadius: 8, fontSize: 12, color: T.magenta }}>
                  You are not in the top 20 at this point.
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RankingRow({ rank, name, rating, reviews, category, isYou }: {
  rank: number; name: string; rating: number | null; reviews: number | null; category: string; isYou: boolean;
}) {
  const rc = rankColor(rank);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
      background: isYou ? 'rgba(18,181,196,0.06)' : T.bgSurface,
      border: `1px solid ${isYou ? T.cyan : T.border}`, borderRadius: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', background: rank > 0 ? rc.bg : '#9aa0a6', color: rank > 0 ? rc.color : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: sharedStyles.fontMono, fontSize: 14, fontWeight: 700, flexShrink: 0,
      }}>{rank > 0 ? rank : 'X'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.fgPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          {isYou && <span style={{ fontSize: 9, fontWeight: 700, color: T.cyan, background: 'rgba(18,181,196,0.12)', border: `1px solid ${T.cyan}`, borderRadius: 8, padding: '1px 6px', flexShrink: 0 }}>YOU</span>}
        </div>
        <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>
          {rating != null && <>★ {rating.toFixed(1)}{reviews != null ? ` (${reviews})` : ''}{category ? ' · ' : ''}</>}
          {category}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.fgMuted, marginBottom: 8 }}>{children}</div>;
}

function ScanningCard({ elapsed, keyword }: { elapsed: number; keyword: string }) {
  const pct = Math.min(95, Math.floor((elapsed / 130) * 100));
  return (
    <Card>
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div style={{ width: 48, height: 48, margin: '0 auto 16px', border: `4px solid ${T.border}`, borderTopColor: T.brandOrange, borderRadius: '50%', animation: 'qa-spin 0.9s linear infinite' }} />
        <div style={{ fontSize: 17, fontWeight: 700 }}>Scanning Google Maps…</div>
        <div style={{ fontSize: 13.5, color: T.fgSecondary, marginTop: 6 }}>
          Checking your rank for <b>{keyword}</b> across 9 grid points.
        </div>
        <div style={{ height: 6, background: T.bgOverlay, borderRadius: 999, margin: '18px auto 8px', maxWidth: 360, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: T.brandGradient, transition: 'width 1s linear' }} />
        </div>
        <div style={{ fontSize: 12, color: T.fgMuted }}>{elapsed}s elapsed · usually 90–150s</div>
      </div>
      <style>{'@keyframes qa-spin{to{transform:rotate(360deg)}}'}</style>
    </Card>
  );
}

// Footer — YEA logo is the ONLY coloured element; all text is muted.
function Footer() {
  return (
    <div style={{ textAlign: 'center', paddingBottom: 44, marginTop: 28 }}>
      <img src="/assets/yea-logo.png" alt="YEA Business" style={{ height: 34, width: 'auto', marginBottom: 12 }} />
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: T.fgMuted, textTransform: 'uppercase' }}>Built by YEA</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.fgSecondary, fontFamily: sharedStyles.fontDisplay, marginTop: 2 }}>YEA Business</div>
      <div style={{ fontSize: 13, color: T.fgMuted, marginTop: 2 }}>AI-Powered Digital Marketing Agency</div>
      <div style={{ fontSize: 13, color: T.fgMuted, marginTop: 2 }}>Google Premier Partner (Top 3%)</div>
    </div>
  );
}

// ── Primitives ────────────────────────────────────────────────────────
function Card({ children }: { children: ReactNode }) {
  return <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 16, padding: 28, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>{children}</div>;
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.fgSecondary, marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
function Stat({ label, value, color, sub }: { label: string; value: string; color: string; sub: string }) {
  return (
    <div style={{ background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 10, color: T.fgMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: sharedStyles.fontMono, fontSize: 24, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}
function ErrorNote({ text }: { text: string }) {
  return <div style={{ background: '#fce8e6', color: '#c5221f', border: '1px solid #f4c7c3', borderRadius: 8, padding: '10px 12px', fontSize: 13, margin: '4px 0 12px' }}>{text}</div>;
}

const inputStyle: CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '11px 14px', fontSize: 15,
  border: `1px solid ${T.border}`, borderRadius: 10, outline: 'none', fontFamily: sharedStyles.fontBody,
};
function primaryBtn(disabled: boolean): CSSProperties {
  return {
    width: '100%', padding: '13px', fontSize: 15, fontWeight: 700, color: '#fff', border: 'none',
    borderRadius: 10, cursor: disabled ? 'default' : 'pointer', marginTop: 6,
    background: disabled ? T.fgMuted : T.brandGradient, opacity: disabled ? 0.8 : 1,
  };
}
const ghostBtn: CSSProperties = {
  padding: '11px 18px', fontSize: 14, fontWeight: 600, color: T.fgSecondary,
  background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 10, cursor: 'pointer',
};
const candidateCard: CSSProperties = {
  textAlign: 'left', padding: '12px 14px', border: `1px solid ${T.border}`, borderRadius: 10,
  background: T.bgSurface, cursor: 'pointer', width: '100%',
};
